/* eslint-disable @typescript-eslint/no-explicit-any */
import { ArrowDown, ArrowUp, Code2, Copy, Database, Filter, Paintbrush, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";

import { Button, Checkbox, Field, Input, Segmented, Select, Switch, Tabs, Textarea, useToast } from "../ds";
import type { PreviewTheme } from "../shell/TvFrameTheme";
import { specFor } from "./catalog";
import { switchToVariant } from "./ChartTypeGallery";
import { FormatPane } from "./FormatPane";
import { WidgetGeneral } from "./WidgetGeneral";
import type { ApplyDisplayPatch } from "./FormatControls";
import type { NumberFormat } from "./numberFormat";
import { LookPicker } from "./LookPicker";
import { PageSizeControl } from "./PageSizeControl";
import { ensureNumberMeasure, type FieldDrag } from "./autoMeasure";
import { FilterPaneEditor, InteractionsEditor, VisualFiltersEditor } from "./InteractionEditors";
import { groupDatasets, useDatasetSchema, useDatasets } from "./LeftPanels";
import { tablesInReport } from "./ModelPanel";
import { SmartPicker } from "./SmartPicker";
import { MIME_FIELD } from "./StudioCanvas";
import type { SDef, SPage, SWidget } from "./model";
import { isMixedTypes, mixedLabels, mixedSettings } from "./multiEdit";

type PTab = "data" | "filters" | "format" | "advanced";
/** The pane keeps its tab when you click from one visual to the next (like Power BI). */
let lastTab: PTab = "data";

/** "age_count" -> "Age count" for measures that have no display name yet. */
export function humanize(name: string): string {
  const t = name.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function Well({ label, hint, items, onRemove, onMove, onDropField, accept, children }: { label: string; hint?: string; items: Array<{ key: string; label: string }>; onRemove: (i: number) => void; onMove?: (i: number, d: -1 | 1) => void; onDropField: (name: string, f: FieldDrag) => void; accept: "dimension" | "measure"; children?: ReactNode }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`st-well${over ? " is-over" : ""}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(MIME_FIELD)) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const raw = e.dataTransfer.getData(MIME_FIELD);
        if (!raw) return;
        e.preventDefault();
        const f = JSON.parse(raw) as FieldDrag;
        if (f.kind === accept || accept === "dimension" || (accept === "measure" && f.numeric)) onDropField(f.name, f);
      }}
    >
      <div className="st-well__label">{label}</div>
      {hint ? <div className="st-well__hint">{hint}</div> : null}
      {items.length === 0 ? <div className="st-well__empty">{accept === "measure" ? "Drop a number column here to show its total" : "Drop a column here to group by"}</div> : null}
      {items.map((it, i) => (
        <div key={it.key + i} className="st-well__item">
          <span className="ds-grow ds-truncate">{it.label}</span>
          {onMove ? (
            <>
              <button className="st-mini" aria-label="Move up" disabled={i === 0} onClick={() => onMove(i, -1)}><ArrowUp size={12} /></button>
              <button className="st-mini" aria-label="Move down" disabled={i === items.length - 1} onClick={() => onMove(i, 1)}><ArrowDown size={12} /></button>
            </>
          ) : null}
          <button className="st-mini" aria-label={`Remove ${it.label}`} onClick={() => onRemove(i)}><X size={12} /></button>
        </div>
      ))}
      {children}
    </div>
  );
}

export function PropertiesPanel({
  mode,
  widget,
  onChange,
  onDelete,
  onDuplicate,
  onReplay,
  def,
  onApplyNumberFormatToPage,
  onApplyDisplayPatch,
  multi,
}: {
  mode: "admin" | "owner";
  widget: SWidget;
  /** 2+ visuals selected: every Format edit is applied to all of them (see multiEdit.ts). `widget` is the primary one. */
  multi?: SWidget[];
  /** The whole report (v2.2: interactions list the report's filters). */
  def?: SDef;
  onChange: (patch: Partial<SWidget>, key?: string) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  /** Ask the canvas to play this visual's entrance animation again. */
  onReplay: () => void;
  /** Format pane: give every visual on the page this number format. */
  onApplyNumberFormatToPage?: (nf: NumberFormat) => void;
  /** Format pane: write a display patch to every matching widget on the page / in the report. */
  onApplyDisplayPatch?: ApplyDisplayPatch;
}) {
  const [tab, setTabState] = useState<PTab>(lastTab);
  const setTab = (t: PTab) => {
    lastTab = t;
    setTabState(t);
  };
  const many = multi && multi.length > 1 ? multi : null;
  const mixedTypes = many ? isMixedTypes(many) : false;
  const mixedNow = many ? mixedLabels(mixedSettings(many)) : [];
  const spec = specFor(widget.type);
  const datasets = useDatasets(mode);
  const reportTables = def ? tablesInReport(def) : [];
  const { fields, measures } = useDatasetSchema(mode, widget.datasetId);
  const q = widget.query ?? {};
  const dims = q.dimensions ?? [];
  const meas = q.measures ?? [];
  const display = widget.display ?? {};
  const toast = useToast();
  const fieldLabel = (n: string) => fields.find((f) => f.source_name === n)?.display_name ?? n;
  const measureLabel = (n: string) => { const m = measures.find((x) => x.name === n); return m?.display_name || humanize(n); };
  const qc = useQueryClient();
  // A number column dropped on / picked for Values becomes its total (autoMeasure.ts).
  const addValue = async (name: string, f?: FieldDrag) => {
    let measure = name;
    const numeric = f?.numeric ?? (!measures.some((m) => m.name === name) && fields.some((x) => x.source_name === name));
    if (numeric && widget.datasetId) {
      const col = fields.find((x) => x.source_name === name);
      try {
        measure = await ensureNumberMeasure(qc, mode, { kind: "dimension", name, datasetId: widget.datasetId, numeric: true, label: col?.display_name ?? name, aggregation: col?.default_aggregation });
      } catch (e) {
        toast.error("Couldn't add the total", e instanceof Error ? e.message : undefined);
        return;
      }
    }
    if (meas.includes(measure)) return;
    const max = spec?.wells.maxValues;
    setQuery({ measures: max === 1 ? [measure] : max && meas.length >= max ? [...meas.slice(0, max - 1), measure] : [...meas, measure] });
  };
  const setQuery = (patch: Record<string, any>) => onChange({ query: { ...q, ...patch } });
  const setDisplay = (patch: Record<string, any>, key?: string) => onChange({ display: { ...display, ...patch } }, key);
  const moveIn = (arr: string[], i: number, d: -1 | 1) => { const n = [...arr]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; };
  const isFilter = spec?.group === "filter";
  const [json, setJson] = useState("");
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  useEffect(() => {
    if (tab === "advanced") setJson(JSON.stringify(widget, null, 2));
  }, [tab, widget]);

  if (many) {
    // Several visuals selected: only the Format pane makes sense (it writes to all of them).
    const typeNames = [...new Set(many.map((m) => specFor(m.type)?.label ?? m.type))];
    return (
      <div className="st-props st-props--visual st-props--multi">
        <div className="st-props__head">
          <span className="ds-stat__icon" style={{ width: 34, height: 34, borderRadius: 9 }}><Paintbrush size={17} /></span>
          <div className="ds-grow" style={{ minWidth: 0 }}>
            <strong>Editing {many.length} widgets</strong>
            <div className="ds-small ds-faint" style={{ marginTop: 3 }}>{mixedTypes ? `${typeNames.length} kinds: ${typeNames.slice(0, 3).join(", ")}${typeNames.length > 3 ? "…" : ""}` : `All ${typeNames[0]}`}</div>
          </div>
        </div>
        <div className="st-multi-note" role="note">
          {mixedTypes ? "These are different kinds of visual, so only the settings they share are shown (title, card, border…). " : "Changes you make here apply to every selected visual. "}
          To change a visual’s data or its own name, click it on its own.
          {mixedNow.length ? <div className="st-multi-mixed"><strong>Different on each:</strong> {mixedNow.slice(0, 6).join(", ")}{mixedNow.length > 6 ? "…" : ""}. The controls show the first visual’s value; changing one sets it on all.</div> : null}
        </div>
        <WidgetGeneral widget={widget} onChange={onChange} multi />
        <FormatPane
          widget={widget}
          seriesNames={[]}
          multi={mixedTypes ? "mixed" : "same"}
          onChange={onChange}
          onSwitchVariant={() => undefined}
          onReplay={onReplay}
          onApplyNumberFormatToPage={onApplyNumberFormatToPage}
          onApplyDisplayPatch={onApplyDisplayPatch}
        />
      </div>
    );
  }

  return (
    <div className="st-props st-props--visual">
      <div className="st-props__head">
        <span className="ds-stat__icon" style={{ width: 34, height: 34, borderRadius: 9 }}>{spec ? <spec.icon size={17} /> : <Code2 size={17} />}</span>
        <div className="ds-grow" style={{ minWidth: 0 }}>
          <Input inputSize="sm" value={widget.title ?? ""} placeholder="Title" onChange={(e) => onChange({ title: e.target.value }, `title-${widget.id}`)} aria-label="Visual title" />
          <div className="ds-small ds-faint" style={{ marginTop: 3 }}>{spec?.label ?? widget.type}</div>
        </div>
        <Button size="sm" variant="ghost" icon={Copy} aria-label="Duplicate (Ctrl+D)" title="Duplicate (Ctrl+D)" onClick={onDuplicate} />
        <Button size="sm" variant="ghost" icon={Trash2} aria-label="Delete (Del)" title="Delete (Del)" onClick={onDelete} />
      </div>
      <WidgetGeneral widget={widget} onChange={onChange} />
      <Tabs<PTab> value={tab} onChange={setTab} tabs={[{ value: "data", label: "Data", icon: Database }, { value: "filters", label: `Filters${q.filters?.length ? ` (${q.filters.length})` : ""}`, icon: Filter }, { value: "format", label: "Format", icon: Paintbrush }, { value: "advanced", label: "Advanced", icon: Code2 }]} />

      {tab === "data" ? (
        <div className="st-props__body">
          {spec && !spec.needsData && widget.type === "text" ? (
            <Field label="Text" hint="Shown as-is on the page.">
              <Textarea value={(display.text as string) ?? (widget.text as string) ?? ""} onChange={(e) => setDisplay({ text: e.target.value }, `text-${widget.id}`)} rows={4} />
            </Field>
          ) : null}
          {widget.type === "html" ? (
            <>
              <Field label="HTML" hint="Use {{Field Name}} or {{measure_name}} to insert live values. Scripts are removed.">
                <Textarea code value={(display.html as string) ?? ""} onChange={(e) => setDisplay({ html: e.target.value }, `html-${widget.id}`)} rows={8} />
              </Field>
              <Field label="CSS" hint="Applies only inside this panel.">
                <Textarea code value={(display.css as string) ?? ""} onChange={(e) => setDisplay({ css: e.target.value }, `css-${widget.id}`)} rows={5} />
              </Field>
            </>
          ) : null}
          {spec?.needsData || widget.type === "html" ? (
            <>
              <Field label="Table" hint={reportTables.length ? undefined : "Add data to this report first (Data → Add data)."}>
                <Select value={widget.datasetId ?? ""} onChange={(e) => onChange({ datasetId: e.target.value || null, query: { ...q, dimensions: [], measures: [], seriesField: null, sort: [] } })}>
                  <option value="">Choose a table…</option>
                  {/* v2.3: only this report's tables (its data model), never every table on the platform. */}
                  {reportTables.length ? groupDatasets((datasets.data ?? []).filter((d) => reportTables.includes(d.id) || d.id === widget.datasetId)).map(([group, items]) => (
                    <optgroup key={group} label={group}>
                      {items.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </optgroup>
                  )) : groupDatasets(datasets.data ?? []).map(([group, items]) => (
                    <optgroup key={group} label={group}>
                      {items.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </optgroup>
                  ))}
                </Select>
              </Field>
              {widget.datasetId ? (
                <>
                  {spec?.wells.field ? (
                    <Well label={spec.wells.field} hint="The column this works on." accept="dimension" items={dims.slice(0, 1).map((d) => ({ key: d, label: fieldLabel(d) }))} onRemove={() => setQuery({ dimensions: [] })} onDropField={(n) => setQuery({ dimensions: [n] })}>
                      <FieldPicker options={fields.map((f) => ({ value: f.source_name, label: f.display_name }))} onPick={(n) => setQuery({ dimensions: [n] })} />
                    </Well>
                  ) : null}
                  {spec?.wells.axis ? (
                    <Well label={spec.wells.axis} hint="Group by: what each bar, slice or row stands for (e.g. vessel, month)." accept="dimension" items={dims.map((d) => ({ key: d, label: fieldLabel(d) }))} onRemove={(i) => setQuery({ dimensions: dims.filter((_, j) => j !== i) })} onMove={(i, d) => setQuery({ dimensions: moveIn(dims, i, d) })} onDropField={(n) => !dims.includes(n) && setQuery({ dimensions: [...dims, n] })}>
                      <FieldPicker options={fields.filter((f) => !dims.includes(f.source_name)).map((f) => ({ value: f.source_name, label: f.display_name }))} onPick={(n) => setQuery({ dimensions: [...dims, n] })} />
                    </Well>
                  ) : null}
                  {spec?.wells.values ? (
                    <Well label={spec.wells.values} hint="Value: the number to show (e.g. total cost)." accept="measure" items={meas.map((m) => ({ key: m, label: measureLabel(m) }))} onRemove={(i) => setQuery({ measures: meas.filter((_, j) => j !== i) })} onMove={(i, d) => setQuery({ measures: moveIn(meas, i, d) })} onDropField={(n, f) => void addValue(n, f)}>
                      <FieldPicker
                        placeholder="Add a number…"
                        options={[
                          ...measures.filter((m) => !meas.includes(m.name)).map((m) => ({ value: m.name, label: m.display_name || humanize(m.name) })),
                          ...fields.filter((x) => x.field_role === "measure" && !measures.some((m) => m.source_field === x.source_name && (m.aggregation ?? "") === (x.default_aggregation || "sum"))).map((x) => ({ value: x.source_name, label: `Total ${x.display_name}` })),
                        ]}
                        onPick={(n) => void addValue(n)}
                      />
                    </Well>
                  ) : null}
                  {!isFilter && spec?.wells.values ? (
                    <SmartPicker mode={mode} widget={widget} fields={fields} measures={measures} onApply={(patch, label) => { onChange(patch); toast.success(`Switched to ${label}`, "Your fields moved with it — Ctrl+Z to undo."); }} />
                  ) : null}
                  {spec?.wells.legend ? (
                    <Field label="Legend (split into series)" hint="Optional — e.g. one colour per company.">
                      <Select value={q.seriesField ?? ""} onChange={(e) => setQuery({ seriesField: e.target.value || null })}>
                        <option value="">None</option>
                        {fields.map((f) => <option key={f.id} value={f.source_name}>{f.display_name}</option>)}
                      </Select>
                    </Field>
                  ) : null}
                  {!isFilter ? (
                    <div className="ds-grid ds-grid--2">
                      <Field label="Sort by">
                        <Select value={q.sort?.[0]?.field ?? ""} onChange={(e) => setQuery({ sort: e.target.value ? [{ field: e.target.value, direction: q.sort?.[0]?.direction ?? "desc" }] : [] })}>
                          <option value="">Default</option>
                          {[...dims.map((d) => ({ v: d, l: fieldLabel(d) })), ...meas.map((m) => ({ v: m, l: measureLabel(m) }))].map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </Select>
                      </Field>
                      <Field label="Order">
                        <Segmented value={(q.sort?.[0]?.direction ?? "desc") as "asc" | "desc"} onChange={(v) => q.sort?.[0] && setQuery({ sort: [{ ...q.sort[0], direction: v }] })} options={[{ value: "asc", label: "A→Z" }, { value: "desc", label: "Z→A" }]} />
                      </Field>
                    </div>
                  ) : null}
                  {!isFilter && widget.type !== "kpi" && widget.type !== "gauge_chart" ? (
                    <Field label="Show at most" hint="Rows or categories.">
                      <Select value={String(q.limit ?? 100)} onChange={(e) => setQuery({ limit: Number(e.target.value) })}>
                        {[5, 10, 12, 20, 50, 100, 250, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
                      </Select>
                    </Field>
                  ) : null}

                </>
              ) : (
                <div className="ds-small ds-muted">Step 1: pick a table above. Step 2: drop columns from the Data tab on the left, or choose them from the lists that appear here.</div>
              )}
            </>
          ) : null}
        </div>
      ) : null}

      {tab === "filters" ? (
        <div className="st-props__body">
          <div className="st-section__label">Filters on this visual</div>
          <p className="ds-small ds-muted">Choose a column. Text columns offer a checklist: keep all items selected, then untick any you want to hide.</p>
          {widget.datasetId ? <VisualFiltersEditor widget={widget} fields={fields} onChange={(patch) => onChange(patch)} /> : <div className="ds-small ds-muted">Choose a table in Data first.</div>}
          <div className="st-section__label" style={{ marginTop: 14 }}>Interactions</div>
          <InteractionsEditor widget={widget} def={def} onChange={(patch) => onChange(patch)} />
        </div>
      ) : null}

      {tab === "format" ? (
        <FormatPane
          widget={widget}
          seriesNames={q.seriesField ? [] : ["donut_chart", "pie_chart", "treemap_chart", "funnel_chart"].includes(widget.type) ? [] : meas.map(measureLabel)}
          onChange={onChange}
          onSwitchVariant={(v) => {
            const { patch, note } = switchToVariant(widget, v);
            onChange(patch);
            if (note) toast.info(`Switched to ${v.label}`, note);
          }}
          onReplay={onReplay}
          onApplyNumberFormatToPage={onApplyNumberFormatToPage}
          onApplyDisplayPatch={onApplyDisplayPatch}
        />
      ) : null}

      {tab === "advanced" ? (
        <div className="st-props__body">
          <div className="ds-small ds-muted">The visual’s full definition. Unknown keys are kept on publish.</div>
          <Textarea code value={json} onChange={(e) => setJson(e.target.value)} rows={22} spellCheck={false} />
          {jsonErr ? <div className="ds-error-text">{jsonErr}</div> : null}
          <Button variant="primary" size="sm" onClick={() => {
            try {
              const parsed = JSON.parse(json);
              if (parsed.id !== widget.id) throw new Error("Keep the same id.");
              setJsonErr(null);
              onChange(parsed);
            } catch (e) {
              setJsonErr((e as Error).message);
            }
          }}>Apply</Button>
        </div>
      ) : null}
    </div>
  );
}

/** Page background colour: an explicit {type:"color"} only; "Default" removes it (the Look decides). */
function PageBackgroundField({ page, onChange }: { page: SPage; onChange: (background: { type: string; value: string } | undefined) => void }) {
  const bg = (page as any).background as { type?: string; value?: string } | undefined;
  const custom = bg?.type === "color" && /^#[0-9a-fA-F]{6}$/.test(bg.value ?? "") ? bg.value! : null;
  return (
    <Field label="Page background" hint="A solid colour behind the visuals on this page. Default uses the report Look.">
      <div className="ds-row" style={{ gap: 8 }}>
        <input type="color" aria-label="Page background colour" value={custom ?? "#0a1628"} onChange={(e) => onChange({ type: "color", value: e.target.value })} style={{ width: 42, height: 30, padding: 0, border: "1px solid var(--border)", borderRadius: 6, background: "none" }} />
        <span className="ds-small ds-faint ds-grow">{custom ?? "Default"}</span>
        <Button size="sm" variant="ghost" disabled={!custom} onClick={() => onChange(undefined)}>Reset</Button>
      </div>
    </Field>
  );
}

function FieldPicker({ options, onPick, placeholder = "Add a field…" }: { options: Array<{ value: string; label: string }>; onPick: (v: string) => void; placeholder?: string }) {
  if (!options.length) return null;
  return (
    <select className="ds-select ds-select--sm st-picker" value="" onChange={(e) => e.target.value && onPick(e.target.value)} aria-label={placeholder}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function PageProperties({ def, page, previewMode, onChange, mode = "admin", onSelectPage }: { def: SDef; page: SPage; previewMode: PreviewTheme; onChange: (next: SDef, key?: string) => void; mode?: "admin" | "owner"; onSelectPage?: (id: string) => void }) {
  const setPage = (patch: Partial<SPage>, key?: string) => onChange({ ...def, pages: def.pages.map((p) => (p.id === page.id ? { ...p, ...patch } : p)) }, key);
  const settings = (def.settings ?? {}) as Record<string, any>;
  return (
    <div className="st-props">
      <div className="st-props__body">
        <div className="st-section__label">Page</div>
        <Field label="Page name"><Input inputSize="sm" value={page.name} onChange={(e) => setPage({ name: e.target.value }, `pn-${page.id}`)} /></Field>
        <Field label="Section" hint="Groups pages into sections on the TV (two-level tabs)."><Input inputSize="sm" value={page.section ?? ""} onChange={(e) => setPage({ section: e.target.value || undefined }, `ps-${page.id}`)} /></Field>
        <PageSizeControl def={def} page={page} onChange={onChange} onSelectPage={onSelectPage} />
        <PageBackgroundField page={page} onChange={(background) => setPage({ background } as Partial<SPage>)} />
        <Checkbox label="Page scrolls when content is taller than the screen" checked={!!page.canvas?.scrollable} onChange={(v) => setPage({ canvas: { ...(page.canvas ?? { width: 1848, height: 944, columns: 24, rowHeight: 20, gap: 8 }), scrollable: v || undefined } })} />
        <div className="ds-divider" />
        <div className="st-section__label">Filter pane — this page</div>
        <FilterPaneEditor mode={mode} def={def} scope="page" pageId={page.id} onChange={onChange} />
        <div className="ds-divider" />
        <div className="st-section__label">Report</div>
        <Field label="Report name"><Input inputSize="sm" value={def.name} onChange={(e) => onChange({ ...def, name: e.target.value }, "report-name")} /></Field>
        <Field label="Look" hint="Sets the cards, fonts and chart colours for the whole report. Visuals you styled yourself keep their own settings.">
          {/* "maridive-dark" is the contract alias of "maridive" and the only
              spelling older TVs in the field know, so Maridive writes it. */}
          <LookPicker value={def.theme} mode={previewMode} onChange={(id) => onChange({ ...def, theme: id === "maridive" ? "maridive-dark" : id })} />
        </Field>
        <Field label="Fit to screen" hint={settings.fit === "fixed" ? "Keeps the exact designed layout and scales it evenly. Screens that aren’t 16:9 show empty bands at the sides." : "Stretches the grid to use the whole screen: visuals get wider or narrower, text keeps its size, no empty bands."}>
          <Segmented value={(settings.fit === "fixed" ? "fixed" : "fill") as "fill" | "fixed"} onChange={(v) => onChange({ ...def, settings: { ...settings, fit: v } })} options={[{ value: "fill", label: "Fill the screen" }, { value: "fixed", label: "Exact layout" }]} />
        </Field>
        <Switch label="Rotate pages automatically on TVs" checked={!!settings.enablePageRotation} onChange={(v) => onChange({ ...def, settings: { ...settings, enablePageRotation: v } })} />
        {settings.enablePageRotation ? (
          <Field label="Seconds per page"><Select value={String(settings.pageRotationSeconds ?? 30)} onChange={(e) => onChange({ ...def, settings: { ...settings, pageRotationSeconds: Number(e.target.value) } })}>{[15, 20, 30, 45, 60, 90, 120].map((n) => <option key={n} value={n}>{n}s</option>)}</Select></Field>
        ) : null}
        <Field label="Data refresh on screens"><Select value={String(def.refreshSeconds ?? 60)} onChange={(e) => onChange({ ...def, refreshSeconds: Number(e.target.value) })}>{[30, 60, 120, 300, 600, 900].map((n) => <option key={n} value={n}>every {n < 60 ? `${n}s` : `${n / 60} min`}</option>)}</Select></Field>
        <div className="st-section__label" style={{ marginTop: 10 }}>Filter pane — all pages</div>
        <FilterPaneEditor mode={mode} def={def} scope="report" pageId={page.id} onChange={onChange} />
        <div className="ds-divider" />
        <div className="ds-small ds-muted">Tip: select a visual to bind its data and style it. Keyboard: arrows move · Shift+arrows resize · Del removes · Ctrl+D duplicates · Ctrl+Z undo.</div>
      </div>
    </div>
  );
}
